import type { ITask, TaskUrgency, TaskImportance } from "../types";
import type { SortFilter } from "../store/slices/tagSlice";

/** Remove duplicate tasks by uuid, keeping the first occurrence. */
export function deduplicateTasks(tasks: ITask[]): ITask[] {
  const seen = new Set<string>();
  return tasks.filter((t) => {
    if (seen.has(t.uuid)) return false;
    seen.add(t.uuid);
    return true;
  });
}

/** Filter tasks by the active tag filter value. */
export function filterByTag(tasks: ITask[], activeTagFilter: string | null): ITask[] {
  if (activeTagFilter === "untagged") {
    return tasks.filter((t) => !t.tag_id);
  }
  if (activeTagFilter) {
    return tasks.filter((t) => t.tag_id === activeTagFilter);
  }
  return tasks;
}

const LEVEL_SCORE: Record<string, number> = { high: 3, medium: 2, low: 1 };

function levelScore(val: TaskUrgency | TaskImportance | null): number {
  return val ? (LEVEL_SCORE[val] ?? 0) : 0;
}

/** Score a due date by proximity: overdue/today tasks get the biggest boost. */
function dueDateScore(dueDate: string | null): number {
  if (!dueDate) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");
  const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 4; // overdue or today
  if (diffDays === 1) return 2; // tomorrow
  if (diffDays <= 7) return 1; // this week
  return 0;
}

/** Sort tasks by the active sort filter. */
export function sortTasks(tasks: ITask[], sort: SortFilter): ITask[] {
  const sorted = [...tasks];

  switch (sort) {
    case "newest":
      return sorted.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    case "oldest":
      return sorted.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    case "urgent":
      return sorted.sort((a, b) => {
        const diff = levelScore(b.urgency) - levelScore(a.urgency);
        if (diff !== 0) return diff;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    case "important":
      return sorted.sort((a, b) => {
        const diff = levelScore(b.importance) - levelScore(a.importance);
        if (diff !== 0) return diff;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    case "priority":
      return sorted.sort((a, b) => {
        const scoreA = levelScore(a.urgency) + levelScore(a.importance) + dueDateScore(a.due_date);
        const scoreB = levelScore(b.urgency) + levelScore(b.importance) + dueDateScore(b.due_date);
        if (scoreB !== scoreA) return scoreB - scoreA;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    default:
      return sorted;
  }
}
